<template>
  <v-app>
    <!-- Top nav -->
    <v-app-bar 
      app 
      fixed 
      flat 
      color="rgba(255, 255, 255, 0.85)" 
      class="glass-header px-4" 
      height="64"
    >
      <v-toolbar-title class="ml-0 ml-md-2" :class="{'d-none': !$vuetify.breakpoint.smAndDown}">
        <router-link to="/reception/1/reception/">
          <v-img src="@/assets/img/logo-solena-black.png" width="100" contain />
        </router-link>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      
      <!-- Session Expiry Alerts -->
      <v-toolbar-title
        class="px-2"
        v-if="minutesToExpire > 0 && minutesToExpire <= 5 && daysToExpire == 0 && hoursToExpire == 0"
      >
        <v-alert icon="mdi-alert" dense outlined type="warning" class="mb-0 text-caption rounded-lg pt-1 pb-1">
          <span class="d-none d-sm-inline">Sesión expira en: <strong>{{ minutesToExpire }}m</strong></span>
          <span class="d-inline d-sm-none">{{ minutesToExpire }}m</span>
        </v-alert>
      </v-toolbar-title>

      <!-- User Profile Menu (Desktop) -->
      <v-menu
        offset-y
        rounded="lg"
        transition="slide-y-transition"
        bottom
        v-if="!$vuetify.breakpoint.smAndDown"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn text rounded v-bind="attrs" v-on="on" class="px-2 text-none">
            <v-avatar color="primary" size="32" class="mr-2">
              <span class="white--text text-caption font-weight-bold">{{ user.name ? user.name.charAt(0) : "" }}</span>
            </v-avatar>
            <span class="font-weight-medium grey--text text--darken-3">{{ user.name }}</span>
            <v-icon right color="grey">mdi-chevron-down</v-icon>
          </v-btn>
        </template>

        <v-card width="300" class="rounded-xl pa-2 user-menu-card" elevation="3">
          <v-list>
            <v-list-item>
              <v-list-item-avatar color="primary" size="40">
                <span class="white--text">{{ user.name ? user.name.charAt(0) : "" }}</span>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ user.name }} {{ user.lastName }}</v-list-item-title>
                <v-list-item-subtitle class="text-caption">{{ user.email }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <v-divider class="my-2"></v-divider>
          <v-list-item link color="error" @click="logOut" class="rounded-lg">
            <v-list-item-icon class="mr-3">
              <v-icon color="error">mdi-logout</v-icon>
            </v-list-item-icon>
            <v-list-item-title class="error--text font-weight-medium">Cerrar Sesión</v-list-item-title>
          </v-list-item>
        </v-card>
      </v-menu>

      <!-- Hamburger for mobile wrapper -->
      <v-btn icon @click.stop="drawer = !drawer" class="ml-2" v-if="$vuetify.breakpoint.smAndDown">
        <v-icon>mdi-menu</v-icon>
      </v-btn>
    </v-app-bar>

    <!-- Left nav -->
    <v-navigation-drawer
      app
      v-model="drawer"
      :permanent="!$vuetify.breakpoint.smAndDown"
      :temporary="$vuetify.breakpoint.smAndDown"
      :mini-variant="!$vuetify.breakpoint.smAndDown"
      expand-on-hover
      floating
      class="elevation-1"
    >
      <div class="d-flex align-center justify-center py-4" v-if="!$vuetify.breakpoint.smAndDown">
         <v-img src="@/assets/img/solena_icon_green.png" max-width="32" contain v-if="!$vuetify.breakpoint.smAndDown" />
      </div>
      <v-list nav class="px-2">
        <!-- User info (Mobile Only) -->
        <v-list-item two-line v-if="$vuetify.breakpoint.smAndDown" class="px-1 mt-2 mb-4">
          <v-list-item-avatar color="primary">
            <span class="white--text headline">{{
              user.name ? user.name.charAt(0) : ""
            }}</span>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title class="text-truncate font-weight-bold">{{ user.name }} {{ user.lastName }}</v-list-item-title>
            <v-list-item-subtitle class="text-caption">{{ user.email }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <v-divider class="mb-2" v-if="$vuetify.breakpoint.smAndDown" />

        <v-list-item-group color="primary">
          <!-- Nav items -->
          <v-list-item
            v-for="(item, index) in items"
            :key="index"
            :to="item.to"
            active-class="highlighted"
            :class="item.path === $route.path ? 'highlighted' : ''"
            class="mb-2 rounded-lg"
          >
            <v-list-item-icon class="mr-4">
              <v-icon :color="$route.path.includes(item.to) ? 'primary' : 'grey darken-1'">{{ item.icon }}</v-icon>
            </v-list-item-icon>

            <v-list-item-content>
              <v-list-item-title class="font-weight-medium" v-text="item.text"></v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>

      <!-- Logout (Mobile Only) -->
      <template v-slot:append v-if="$vuetify.breakpoint.smAndDown">
        <div class="pa-4">
          <v-btn block color="error" @click="logOut" rounded outlined>
            <v-icon left size="18">mdi-logout</v-icon>
            Cerrar Sesión
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- Content -->
    <v-main class="main_app" :class="{'mobile-nav-spacing': $vuetify.breakpoint.smAndDown}">
      <v-container
        fluid
        :class="{
          'large-devices': $vuetify.breakpoint.xlOnly,
        }"
        class="pt-4"
      >
        <router-view></router-view>
        <SessionDialog :dialog="dialog" />
      </v-container>
    </v-main>

    <v-dialog
      transition="dialog-top-transition"
      max-width="400"
      v-model="dialog_unauthorized"
      persistent
    >
      <template v-slot:default="dialog">
        <v-card>
          <v-toolbar color="warning" dark>La sesión ha expirado</v-toolbar>
          <v-card-text class="py-0">
            <div class="text-body-4 py-6">
              Puedes volver a iniciar sesión o recargar la página si es que ya
              lo hiciste.
            </div>
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn text @click="$router.go()">Recarregar</v-btn>
            <v-btn text @click="logOut()">Volver a iniciar sesión</v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>

    <!-- Bottom Mobile Nav -->
    <v-bottom-navigation
      app
      fixed
      color="primary"
      v-if="$vuetify.breakpoint.smAndDown"
      class="rounded-t-xl mobile-bottom-nav"
      elevation="20"
      grow
    >
      <v-btn to="/reception/1/reception/" value="dashboard">
        <span class="text-caption">Dash</span>
        <v-icon>mdi-view-dashboard</v-icon>
      </v-btn>

      <v-btn to="/users" value="users">
        <span class="text-caption">Users</span>
        <v-icon>mdi-account-multiple-plus</v-icon>
      </v-btn>

      <v-btn to="/farms" value="farms">
        <span class="text-caption">Agrícolas</span>
        <v-icon>mdi-home-silo</v-icon>
      </v-btn>

      <v-btn to="/logistic" value="logistic">
        <span class="text-caption">Logística</span>
        <v-icon>mdi-truck-delivery</v-icon>
      </v-btn>
    </v-bottom-navigation>

    <!-- Footer -->
    <v-footer
      app
      inset
      padless
      height="32"
      color="transparent"
      class="mb-2"
      v-if="!$vuetify.breakpoint.smAndDown"
    >
      <v-col class="text-center py-0 text-caption grey--text text--darken-1" cols="12">
        <span class="font-weight-light">D.R. © Solena Ag, Inc. {{ new Date().getFullYear() }}</span>
      </v-col>
    </v-footer>
  </v-app>
</template>

<script>
import { mapActions, mapMutations } from "vuex";
import SessionDialog from "@/components/utils/SessionDialog.vue";

export default {
  data() {
    return {
      drawer: false,
      title: "Solena Lab",
      user: {},
      items: [
        {
          icon: "mdi-view-dashboard",
          text: "Dashboard",
          to: "/reception/1/reception/",
        },
        {
          icon: "mdi-account-multiple-plus",
          text: "Usuarios",
          to: "/users",
        },
        {
          icon: "mdi-home-silo",
          text: "Agricolas",
          to: "/farms",
        },

        {
          icon: "mdi-clipboard-edit-outline",
          text: "Logistica",
          to: "/logistic",
        },
      ],
      authItems: [
        {
          icon: "mdi-logout",
          text: "Cerrar sesión",
          action: this.logOut,
        },
      ],
      dialog: true,
      actuallyDate: new Date(),
      expirationDate: new Date(localStorage.getItem("token_expiry")),
      daysToExpire: 0,
      hoursToExpire: 0,
      minutesToExpire: 10,
      dialog_unauthorized: false,
    };
  },
  async mounted() {
    this.getUserInfo();
    await this.getExpirationTime();
    await this.setNewUserInfo();
    window.setInterval(() => {
      this.actuallyDate = new Date();
      // }, (1000 * 60) * 5 )
    }, 1000 * 10);
  },
  async beforeUpdate() {
    let now = new Date();
    const token_expiry = new Date(localStorage.getItem("token_expiry"));

    if (token_expiry < now) {
      // try {
      //     await this.getNewToken(refreshToken);
      // }
      // catch (error) {
      //     console.log("Error getting new token", error);
      // }

      this.dialog_unauthorized = true;
    }
  },
  methods: {
    ...mapActions("session", ["logout", "getNewToken", "updateInfo"]),
    ...mapMutations("session", ["SET_AUTHENTICATED_USER"]),
    // ...mapMutations("login", ["SET_ERROR"]),
    async logOut() {
      await this.logout({
        refreshToken: localStorage.getItem("refresh_token"),
        session: localStorage.getItem("session"),
      });
      this.$router.push("/login");
    },
    getUserInfo() {
      this.user = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user"))
        : "";
    },
    async getExpirationTime() {
      var diffMs = this.expirationDate - this.actuallyDate; // milliseconds between now & Christmas
      var diffDays = Math.floor(diffMs / 86400000); // days
      var diffHrs = Math.floor((diffMs % 86400000) / 3600000); // hours
      var diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000); // minutes

      if (
        this.daysToExpire != diffDays ||
        this.hoursToExpire != diffHrs ||
        this.minutesToExpire != diffMins
      ) {
        this.daysToExpire = diffDays;
        this.hoursToExpire = diffHrs;
        this.minutesToExpire = diffMins;
      }

      // console.log(diffDays + " days, " + diffHrs + " hours, " + diffMins + " minutes until token expires");
    },
    async setNewUserInfo() {
      if (this.user) {
        try {
          let response = await this.updateInfo({ userId: this.user._id });
          if (response && response.user) {
            if (response.user.isExpire) {
              this.dialog_unauthorized = true;
              return;
            }
            this.user = response.user;
            this.SET_AUTHENTICATED_USER(response.user);
          }
        } catch (error) {
          console.log(error);
        }
      }
    },
  },
  watch: {
    async actuallyDate() {
      // console.log(this.actuallyDate)
      await this.getExpirationTime();
      await this.setNewUserInfo();
    },
  },
  components: { SessionDialog },
};
</script>

<style scoped>
.main_app {
  background-color: var(--bg-app);
  min-height: 100vh;
  padding-bottom: 40px; /* Space for footer on desktop */
}

.mobile-nav-spacing {
  padding-bottom: 70px !important; /* Extra space to prevent bottom-nav overlap */
}

.glass-header {
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
}

.mobile-bottom-nav {
  box-shadow: 0px -4px 20px rgba(0, 0, 0, 0.08) !important;
  border-top: 1px solid rgba(0,0,0,0.03);
}

.highlighted {
  background-color: rgba(0, 168, 89, 0.1);
}

/* Ensure the mini-variant drawer shows tooltips or is clear */
.v-navigation-drawer--mini-variant .v-list-item__title {
  opacity: 0;
}

/* Let the full name/email wrap instead of truncating with an ellipsis */
.user-menu-card ::v-deep .v-list-item__title,
.user-menu-card ::v-deep .v-list-item__subtitle {
  white-space: normal;
  overflow-wrap: break-word;
}
</style>
